import * as React from 'react';
import {
    Box,
    CircularProgress,
    Typography,
    Stack
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';

import Api from '../services/Api';

// ---------------------------------
// Props
// ---------------------------------

interface ITvLeaderboardProps {
    theme: Theme;
}

// ---------------------------------
// DTOs
// ---------------------------------

export interface IMiniGamePointsDto {
    miniGameId: number;
    miniGameName: string | null;
    points: number;
}

export interface IPlayerClassificationDto {
    playerId: number;
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    totalPoints: number;
    miniGamePoints: IMiniGamePointsDto[] | null;
}

export interface ITournamentDto {
    id: number;
    name: string | null;
    description?: string | null;
    imageUrl?: string | null;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
}

export interface IBannerDto {
    id: number;
    tournamentId: number;
    company: string | null;
    imageUrl: string | null;
    duration: number; // ms
    priority: number; // 1-100
}

// ---------------------------------
// Tipos para movimientos
// ---------------------------------

type PlayerMovement = 'up' | 'down' | 'same' | 'new';

interface ITvLeaderboardState {
    tournamentId: number | null;
    tournamentName: string;
    classifications: IPlayerClassificationDto[];
    loading: boolean;
    error: string | null;
    movementByPlayer: { [playerId: number]: PlayerMovement };
    lastUpdated: string | null;

    // Banners
    banners: IBannerDto[];
    activeBanner: IBannerDto | null;
    isGlobal: boolean;
}

// ---------------------------------
// Página de Leaderboard para TV
// ---------------------------------

export class TvLeaderboardPageInner extends React.Component<
    ITvLeaderboardProps,
    ITvLeaderboardState
> {
    private pollHandle: number | null = null;
    private lastPositionsByMode: {
        tournament: { [playerId: number]: number };
        global: { [playerId: number]: number };
    } = {
        tournament: {},
        global: {}
    };

    // Rotación de banners
    private bannerTimeoutHandle: number | null = null;
    private lastBannerId: number | null = null;

    public constructor(props: ITvLeaderboardProps) {
        super(props);

        this.state = {
            tournamentId: null,
            tournamentName: 'Clasificación',
            classifications: [],
            loading: true,
            error: null,
            movementByPlayer: {},
            lastUpdated: null,

            banners: [],
            activeBanner: null,
            isGlobal: false
        };

        this.loadTournament = this.loadTournament.bind(this);
        this.loadBanners = this.loadBanners.bind(this);
    }

    public componentDidMount(): void {
        const tournamentId = this.getTournamentIdFromQuery();

        if (!tournamentId) {
            this.setState({
                loading: false,
                error: 'No se ha especificado el torneo (tournamentId) en la URL.'
            });
            return;
        }

        this.setState({ tournamentId }, () => {
            this.loadTournament(tournamentId);
            this.loadLeaderboard(false, tournamentId);
            this.loadBanners(tournamentId);

            this.pollHandle = window.setInterval(() => {
                const nextIsGlobal = !this.state.isGlobal;
                this.loadLeaderboard(nextIsGlobal);
            }, 10000);
        });
    }

    public componentWillUnmount(): void {
        if (this.pollHandle !== null) {
            window.clearInterval(this.pollHandle);
        }
        if (this.bannerTimeoutHandle !== null) {
            window.clearTimeout(this.bannerTimeoutHandle);
        }
    }

    private async loadLeaderboard(
        isGlobal: boolean,
        explicitTournamentId?: number
    ): Promise<void> {
        const tournamentId = explicitTournamentId ?? this.state.tournamentId;

        if (!isGlobal && !tournamentId) {
            return;
        }

        try {
            const endpoint = isGlobal
                ? '/api/Classifications'
                : `/api/Classifications/tournament/${tournamentId}`;

            const response = await Api.get(endpoint);

            if (!response.ok) {
                throw new Error(
                    isGlobal
                        ? 'No se pudo cargar la clasificación general.'
                        : 'No se pudo cargar la clasificación del torneo.'
                );
            }

            const data: IPlayerClassificationDto[] = await response.json();

            data.sort((a, b) => b.totalPoints - a.totalPoints);

            const modeKey = isGlobal ? 'global' : 'tournament';
            const previousPositions = this.lastPositionsByMode[modeKey];

            const movementByPlayer: { [playerId: number]: PlayerMovement } = {};
            const newPositions: { [playerId: number]: number } = {};

            data.forEach((player, index) => {
                const prevPos = previousPositions[player.playerId];
                newPositions[player.playerId] = index;

                if (prevPos === undefined) {
                    movementByPlayer[player.playerId] = 'new';
                } else if (prevPos > index) {
                    movementByPlayer[player.playerId] = 'up';
                } else if (prevPos < index) {
                    movementByPlayer[player.playerId] = 'down';
                } else {
                    movementByPlayer[player.playerId] = 'same';
                }
            });

            this.lastPositionsByMode[modeKey] = newPositions;

            this.setState({
                classifications: data,
                loading: false,
                error: null,
                movementByPlayer,
                lastUpdated: this.formatTime(new Date()),
                isGlobal
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error:
                    error?.message ||
                    (isGlobal
                        ? 'Error al cargar la clasificación general.'
                        : 'Error al cargar la clasificación del torneo.')
            });
        }
    }

    // ---------------------------------
    // Helpers
    // ---------------------------------

    private getTournamentIdFromQuery(): number | null {
        const params = new URLSearchParams(window.location.search);
        const tid = params.get('tournamentId');
        if (!tid) return null;

        const value = parseInt(tid, 10);
        return Number.isNaN(value) ? null : value;
    }

    private formatTime(date: Date): string {
        return date.toLocaleTimeString();
    }

    private getDisplayName(player: IPlayerClassificationDto): string {
        if (player.fullName && player.fullName.trim().length > 0) {
            return player.fullName;
        }

        const composed = `${player.firstName || ''} ${player.lastName || ''
            }`.trim();
        return composed.length > 0
            ? composed
            : `Jugador ${player.playerId}`;
    }

    // ---------------------------------
    // Carga de torneo y clasificación
    // ---------------------------------

    private async loadTournament(tournamentId: number): Promise<void> {
        try {
            const response = await Api.get(
                `/api/Tournaments/${tournamentId}`
            );

            if (!response.ok) {
                return;
            }

            const data: ITournamentDto = await response.json();

            this.setState({
                tournamentName: data.name || 'Clasificación'
            });
        } catch {
            // Ignoramos errores de torneo, dejamos nombre genérico
        }
    }

    // ---------------------------------
    // BANNERS: carga y rotación
    // ---------------------------------

    private async loadBanners(
        tournamentId: number
    ): Promise<void> {
        try {
            const response = await Api.get(
                `/api/Banners/by-tournament/${tournamentId}`
            );

            if (!response.ok) {
                return;
            }

            const data: IBannerDto[] = await response.json();

            // Filtramos banners sin imagen
            const valid = data.filter(
                (b) => b.imageUrl && b.imageUrl.trim().length > 0
            );

            console.log('loadBanners', valid);

            this.setState(
                {
                    banners: valid
                },
                () => {
                    if (valid.length > 0) {
                        this.startBannerRotation();
                    }
                }
            );
        } catch {
            // Si falla la carga de banners, simplemente no mostramos ninguno
        }
    }

    private startBannerRotation(): void {
        if (this.bannerTimeoutHandle !== null) return; // ya hay rotación
        if (!this.state.banners || this.state.banners.length === 0)
            return;

        // Espera inicial antes del primer banner
        const initialDelay = 60000; // 10s

        this.bannerTimeoutHandle = window.setTimeout(
            () => this.showNextBanner(),
            initialDelay
        );
    }

    private showNextBanner(): void {
        const { banners } = this.state;
        if (!banners || banners.length === 0) {
            this.bannerTimeoutHandle = null;
            return;
        }

        const next = this.pickNextBanner();
        if (!next) {
            this.bannerTimeoutHandle = null;
            return;
        }

        this.setState(
            { activeBanner: next },
            () => {
                const safeDuration =
                    next.duration && next.duration > 1000
                        ? next.duration
                        : 5000; // fallback 5s

                console.log('safeDuration', safeDuration);
                // Cuando termine la duración del banner, lo ocultamos y programamos el siguiente
                if (this.bannerTimeoutHandle !== null) {
                    window.clearTimeout(this.bannerTimeoutHandle);
                }

                this.bannerTimeoutHandle = window.setTimeout(
                    () => this.hideBannerAndScheduleNext(),
                    safeDuration
                );
            }
        );
    }

    private hideBannerAndScheduleNext(): void {
        this.setState(
            { activeBanner: null },
            () => {
                const gapBetweenBanners = 60000; // 8s sin banner antes del siguiente

                if (this.bannerTimeoutHandle !== null) {
                    window.clearTimeout(this.bannerTimeoutHandle);
                }

                this.bannerTimeoutHandle = window.setTimeout(
                    () => this.showNextBanner(),
                    gapBetweenBanners
                );
            }
        );
    }

    private pickNextBanner(): IBannerDto | null {
        const { banners } = this.state;
        if (!banners || banners.length === 0) return null;

        // Pesos por prioridad (mínimo 1)
        const weights = banners.map((b) =>
            b.priority && b.priority > 0 ? b.priority : 1
        );
        let total = weights.reduce((acc, w) => acc + w, 0);
        if (total <= 0) total = banners.length;

        let attempts = 0;
        while (attempts < 5) {
            let r = Math.random() * total;
            for (let i = 0; i < banners.length; i++) {
                if (r < weights[i]) {
                    const chosen = banners[i];

                    if (
                        chosen.id !== this.lastBannerId ||
                        banners.length === 1
                    ) {
                        this.lastBannerId = chosen.id;
                        return chosen;
                    } else {
                        attempts++;
                        break;
                    }
                }
                r -= weights[i];
            }
        }

        return banners[0];
    }

    // ---------------------------------
    // Fondo (tema o banner activo)
    // ---------------------------------

    private renderBubbles(): React.ReactNode {
        const bubbles = [
            { size: 260, left: '10%', top: '65%', duration: 26, background:  'rgba(207,205,74,1)', transparent: '65%'},
            { size: 320, left: '70%', top: '70%', duration: 32, background:  'rgba(207, 138, 74, 1)', transparent: '65%' },
            { size: 220, left: '80%', top: '20%', duration: 24, background:  'rgba(207,205,74,1)', transparent: '65%' },
            { size: 280, left: '25%', top: '15%', duration: 30, background:  'rgba(2, 148, 14, 1)', transparent: '65%' },
            { size: 200, left: '45%', top: '80%', duration: 28, background:  'rgba(207,205,74,1)', transparent: '65%' },
            { size: 400, left: '5%', top: '20%', duration: 46, background:  'rgba(114, 74, 207, 1)', transparent: '65%' }
        ];

        return bubbles.map((b, index) => (
            <Box
                key={index}
                sx={{
                    position: 'absolute',
                    width: b.size,
                    height: b.size,
                    left: b.left,
                    top: b.top,
                    borderRadius: '50%',
                    background:
                        `radial-gradient(circle, ${b.background}, transparent ${b.transparent})`,
                    filter: 'blur(45px)',
                    opacity: 1,

                    // ANIMACIÓN COMPLETA – CRUZAN LA PANTALLA
                    animation: `bigFloat ${b.duration}s linear infinite`,
                    animationDelay: `${index * 4}s`,

                    '@keyframes bigFloat': {
                        '0%': {
                            transform: 'translate3d(-30vw, 20vh, 0) scale(1.9)',
                            opacity: 0.35
                        },
                        '25%': {
                            transform: 'translate3d(10vw, -10vh, 0) scale(2.3)',
                            opacity: 0.65
                        },
                        '50%': {
                            transform: 'translate3d(30vw, -25vh, 0) scale(3.0)',
                            opacity: 1
                        },
                        '75%': {
                            transform: 'translate3d(10vw, 5vh, 0) scale(2.3)',
                            opacity: 0.65
                        },
                        '100%': {
                            transform: 'translate3d(-30vw, 20vh, 0) scale(1.6)',
                            opacity: 0.35
                        }
                    }
                }}
            />
        ));
    }

    private renderBackground(): React.ReactNode {
        const { theme } = this.props;
        const { activeBanner } = this.state;

        // 1) Si hay banner activo, usamos su imagen como fondo a pantalla completa
        if (activeBanner && activeBanner.imageUrl) {
            return (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 0,
                        backgroundImage: `url(${activeBanner.imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'right center',
                        backgroundRepeat: 'no-repeat'
                    }}
                >
                    {/* overlay para legibilidad */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                                'linear-gradient(90deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.25) 100%)'
                        }}
                    />
                </Box>
            );
        }

        // 2) Si no hay banner, usamos el sistema de fondos del tema
        const lb = (theme as any).leaderBoard || {
            backgroundMode: 'gradient'
        };

        switch (lb.backgroundMode) {
            case 'video':
                return (
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 0,
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            component="video"
                            autoPlay
                            muted
                            loop
                            playsInline
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        >
                            {lb.videoSrc && (
                                <source src={lb.videoSrc} type="video/mp4" />
                            )}
                        </Box>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'linear-gradient(180deg, rgba(0,0,0,0.5), rgba(0,0,0,0.9))'
                            }}
                        />
                    </Box>
                );

            case 'bubbles':
                return (
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 0,
                            background: '#000',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'radial-gradient(circle at 20% 0%, #003b3b 0, #000000 55%, #101010 100%)'
                            }}
                        />
                        {this.renderBubbles()}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.8))'
                            }}
                        />
                    </Box>
                );

            case 'image':
                return (
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 0,
                            backgroundImage: lb.imageSrc
                                ? `url(${lb.imageSrc})`
                                : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'linear-gradient(180deg, rgba(0,0,0,0.5), rgba(0,0,0,0.9))'
                            }}
                        />
                    </Box>
                );

            case 'gradient':
            default:
                return (
                    <>
                        <Box
                            sx={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 0,
                                background:
                                    'radial-gradient(circle at 0% 0%, #003b3b 0, #000000 55%, #101010 100%)',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    background:
                                        'radial-gradient(circle at 100% 0, rgba(207,205,74,0.18), transparent 55%)'
                                }
                            }}
                        />
                        <Box
                            sx={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 0,
                                background:
                                    'linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0.9))'
                            }}
                        />
                    </>
                );
        }
    }

    // ---------------------------------
    // Render de secciones
    // ---------------------------------

    private renderMiniGamePoints(
        miniGamePoints: IMiniGamePointsDto[] | null
    ): React.ReactNode {
        if (!miniGamePoints || miniGamePoints.length === 0) {
            return (
                <Typography
                    variant="body2"
                    sx={{ opacity: 0.6, fontSize: '0.8rem' }}
                >
                    Sin puntos por minijuego todavía.
                </Typography>
            );
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1
                }}
            >
                {miniGamePoints.map((mp) => (
                    <Box
                        key={mp.miniGameId}
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: 'rgba(0, 0, 0, 0.4)'
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontSize: '0.8rem' }}
                        >
                            {mp.miniGameName || `Juego ${mp.miniGameId}`}:{' '}
                            <strong>{mp.points}</strong> pts
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }

    private getRowAccent(
        index: number,
        movement: PlayerMovement
    ): {
        borderColor: string;
        background: string;
        shadow: string;
    } {
        let borderColor = 'rgba(255, 255, 255, 0.2)';
        let background =
            'linear-gradient(90deg, rgba(0,0,0,0.35), rgba(0,0,0,0.15))';
        let shadow = '0 8px 18px rgba(0,0,0,0.4)';

        if (index === 0) {
            borderColor = '#CFCD4A';
            background =
                'linear-gradient(90deg, rgba(207,205,74,0.22), rgba(0,0,0,0.55))';
            shadow = '0 12px 24px rgba(0,0,0,0.7)';
        } else if (index === 1) {
            borderColor = 'rgba(224,224,224,0.8)';
            background =
                'linear-gradient(90deg, rgba(200,200,200,0.22), rgba(0,0,0,0.55))';
        } else if (index === 2) {
            borderColor = 'rgba(205,150,80,0.9)';
            background =
                'linear-gradient(90deg, rgba(205,150,80,0.22), rgba(0,0,0,0.55))';
        }

        if (movement === 'up') {
            borderColor = '#4caf50';
            shadow = '0 0 25px rgba(76,175,80,0.7)';
        } else if (movement === 'down') {
            borderColor = '#f44336';
            shadow = '0 0 25px rgba(244,67,54,0.7)';
        } else if (movement === 'new') {
            borderColor = '#29b6f6';
            shadow = '0 0 25px rgba(41,182,246,0.7)';
        }

        return { borderColor, background, shadow };
    }

    private renderRow(
        player: IPlayerClassificationDto,
        index: number
    ): React.ReactNode {
        const { movementByPlayer } = this.state;
        const movement = movementByPlayer[player.playerId] || 'same';
        const displayName = this.getDisplayName(player);
        const position = index + 1;

        const { borderColor, background, shadow } = this.getRowAccent(
            index,
            movement
        );

        return (
            <motion.div
                key={player.playerId}
                layout
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ type: 'spring', stiffness: 80, damping: 18 }}
            >
                <Box
                    sx={{
                        border: `2px solid ${borderColor}`,
                        background,
                        boxShadow: shadow,
                        px: 2.5,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1.5
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ flex: 1 }}
                    >
                        <Box
                            sx={{
                                width: 54,
                                height: 54,
                                borderRadius: '50%',
                                bgcolor:
                                    index === 0
                                    ? '#D6AF36'
                                    : index === 1
                                        ? '#A7A7AD'
                                        : index === 2
                                            ? '#A77044'
                                            : 'secondary.light',
                                color: index === 0 ? 'black' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: '1.4rem',
                                border:
                                    index === 0
                                        ? '3px solid rgba(255,255,255,0.9)'
                                        : '2px solid rgba(255,255,255,0.4)'
                            }}
                        >
                            {position}
                        </Box>

                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: {
                                        xs: '1.1rem',
                                        sm: '1.3rem',
                                        md: '1.5rem'
                                    },
                                    fontWeight: 700,
                                    letterSpacing: '0.03em'
                                }}
                            >
                                {displayName}
                            </Typography>

                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mt: 0.3 }}
                            >
                                {movement === 'up' && (
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        sx={{ color: '#4caf50' }}
                                    >
                                        <ArrowDropUpIcon />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Subiendo
                                        </Typography>
                                    </Stack>
                                )}
                                {movement === 'down' && (
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        sx={{ color: '#f44336' }}
                                    >
                                        <ArrowDropDownIcon />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Bajando
                                        </Typography>
                                    </Stack>
                                )}
                                {movement === 'new' && (
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        sx={{ color: '#29b6f6' }}
                                    >
                                        <SportsGolfIcon sx={{ fontSize: 20 }} />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Nuevo en la tabla
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>

                            {this.renderMiniGamePoints(player.miniGamePoints)}
                        </Box>
                    </Stack>

                    <Box
                        sx={{
                            textAlign: 'right',
                            minWidth: { xs: 120, sm: 160 },
                            ml: 2
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                textTransform: 'uppercase',
                                fontSize: '0.8rem',
                                opacity: 0.7,
                                letterSpacing: '0.15em'
                            }}
                        >
                            Puntos
                        </Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: {
                                    xs: '1.8rem',
                                    sm: '2.2rem',
                                    md: '2.6rem'
                                },
                                fontWeight: 800,
                                lineHeight: 1.1,
                                color: '#CFCD4A'
                            }}
                        >
                            {player.totalPoints}
                        </Typography>
                    </Box>
                </Box>
            </motion.div>
        );
    }

    private renderContent(): React.ReactNode {
        const { classifications, loading, error } = this.state;

        if (loading && classifications.length === 0) {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <CircularProgress size={64} />
                </Box>
            );
        }

        if (error && classifications.length === 0) {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="h6" color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (!classifications || classifications.length === 0) {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{ opacity: 0.8, textAlign: 'center' }}
                    >
                        No hay jugadores en la clasificación todavía.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
            flex: 1,
            mt: { xs: 2, sm: 3 },
            maxWidth: 1200,
            width: '100%',

            // 👇 aquí activamos scroll vertical
            maxHeight: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 260px)' },
            overflowY: 'auto',
            pr: 1,

            // 👇 ocultar scrollbar en WebKit (Chrome, Edge, Safari)
            '&::-webkit-scrollbar': {
                width: 0,
                height: 0
            },
            '&::-webkit-scrollbar-thumb': {
                background: 'transparent'
            },
            '&::-webkit-scrollbar-track': {
                background: 'transparent'
            },

            // 👇 ocultar scrollbar en Firefox
            scrollbarWidth: 'none',

            // 👇 ocultar scrollbar en IE/Edge Legacy
            msOverflowStyle: 'none'
        }}
            >
                <AnimatePresence>
                    {classifications.map((p, index) =>
                        this.renderRow(p, index)
                    )}
                </AnimatePresence>
            </Box>
        );
    }

    // ---------------------------------
    // Render principal
    // ---------------------------------

    public render(): React.ReactElement {
        const { tournamentName, lastUpdated, activeBanner } = this.state;
        const isBannerMode = !!(activeBanner && activeBanner.imageUrl);

        return (
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    width: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    bgcolor: 'transparent',
                    color: 'white'
                }}
            >
                {/* Fondo dinámico (tema o banner) */}
                {this.renderBackground()}

                {/* Contenido principal */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isBannerMode ? 'flex-start' : 'center',
                        justifyContent: isBannerMode ? 'center' : 'flex-start',
                        px: { xs: 2, sm: 4, md: 6 },
                        py: { xs: 2, sm: 3, md: 4 }
                    }}
                >
                    <AnimatePresence mode="wait">
                        {
                            isBannerMode ? (
                                <motion.div
                                    key="banner-mode"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'flex-start'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: '35vw' },
                                            maxWidth: 900,
                                            ml: { xs: 0, md: '4vw' },
                                            mr: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.5,
                                            borderRadius: 3,
                                            border: '2px solid rgba(255,255,255,0.9)',
                                            backgroundColor: 'rgba(0, 0, 0, 0.92)',
                                            boxShadow: '0 20px 50px rgba(0,0,0,0.9)',
                                            backdropFilter: 'blur(6px)',
                                            padding: 2
                                        }}
                                    >
                                        {/* Cabecera */}
                                        <Box>
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                                sx={{ mb: 0.5 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 56,
                                                        height: 56,
                                                        borderRadius: '18px',
                                                        bgcolor: '#CFCD4A',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow:
                                                            '0 0 25px rgba(207,205,74,0.8)'
                                                    }}
                                                >
                                                    <EmojiEventsIcon
                                                        sx={{ color: 'black', fontSize: 36 }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="h3"
                                                        sx={{
                                                            fontSize: {
                                                                xs: '1.8rem',
                                                                sm: '2.4rem',
                                                                md: '2.8rem',
                                                                lg: '3.1rem'
                                                            },
                                                            fontWeight: 800,
                                                            letterSpacing: '0.06em',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        {this.state.isGlobal ? 'Clasificación general' : tournamentName}
                                                    </Typography>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            opacity: 0.8,
                                                            letterSpacing: '0.18em',
                                                            textTransform: 'uppercase',
                                                            fontSize: {
                                                                xs: '0.7rem',
                                                                sm: '0.8rem'
                                                            }
                                                        }}
                                                    >
                                                        Clasificación en tiempo real
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            {lastUpdated && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        opacity: 0.7,
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    Última actualización: {lastUpdated}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Lista de jugadores */}
                                        {this.renderContent()}
                                    </Box>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="normal-mode"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: '100%' },
                                            maxWidth: 1200,
                                            ml: 'auto',
                                            mr: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.5
                                        }}
                                    >
                                        {/* Cabecera */}
                                        <Box>
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                                sx={{ mb: 0.5 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 56,
                                                        height: 56,
                                                        borderRadius: '18px',
                                                        bgcolor: '#CFCD4A',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow:
                                                            '0 0 25px rgba(207,205,74,0.8)'
                                                    }}
                                                >
                                                    <EmojiEventsIcon
                                                        sx={{ color: 'black', fontSize: 36 }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="h3"
                                                        sx={{
                                                            fontSize: {
                                                                xs: '1.8rem',
                                                                sm: '2.4rem',
                                                                md: '2.8rem',
                                                                lg: '3.1rem'
                                                            },
                                                            fontWeight: 800,
                                                            letterSpacing: '0.06em',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        {this.state.isGlobal ? 'Clasificación general' : tournamentName}
                                                    </Typography>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            opacity: 0.8,
                                                            letterSpacing: '0.18em',
                                                            textTransform: 'uppercase',
                                                            fontSize: {
                                                                xs: '0.7rem',
                                                                sm: '0.8rem'
                                                            }
                                                        }}
                                                    >
                                                        Clasificación en tiempo real
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            {lastUpdated && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        opacity: 0.7,
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    Última actualización: {lastUpdated}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Lista de jugadores */}
                                        {this.renderContent()}
                                    </Box>
                                </motion.div>
                            )
                        }
                    </AnimatePresence>
                </Box>
            </Box>
        );
    }
}

// Wrapper para inyectar el theme en una clase
const TvLeaderboardPageWrapper: React.FC = () => {
    const theme = useTheme();
    return <TvLeaderboardPageInner theme={theme} />;
};

export default TvLeaderboardPageWrapper;
